import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { shouldUseOzonetelSDK } from './ozonetel-dialer-mode';
import { OzonetelSDKCoreService } from './ozonetel-sdk-core.service';

declare var OneCxi: any;

@Injectable({
  providedIn: 'root'
})
export class OzonetelService {
  private oneCXI: any;
  private isInitialized: boolean = false;
  private isStreaming: boolean = false;
  private userName: string;
  private apiKey: string;
  private didNumbers: string[] = [];
  private sipUrl: string;
  private extension: string;
  private ozonetelDomain = 'in1-ccaas-api.ozonetel.com';

  constructor(
    private http: HttpClient,
    private ozonetelSDKCore: OzonetelSDKCoreService
  ) { }

  initializeSDK(userName: string, apiKey: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        console.log('OzonetelService: Checking OneCxi availability...');
        console.log('OneCxi type:', typeof OneCxi);
        console.log('OneCxi value:', OneCxi);

        if (typeof OneCxi === 'undefined') {
          console.error('OneCxi SDK not loaded in window object');
          reject('OneCxi SDK not loaded - check if script is included in index.html');
          return;
        }

        console.log('Creating new OneCxi instance...');
        this.oneCXI = new OneCxi();
        this.userName = userName;
        this.apiKey = apiKey;

        console.log('Calling initializeSDK on OneCxi...');
        const data = this.oneCXI.initializeSDK(userName, apiKey);
        console.log('SDK initialization response:', data);

        if (data) {
          this.sipUrl = data.sip_url;
          this.didNumbers = data.did_numbers || [];
          this.extension = data.extension;
          this.isInitialized = true;

          console.log('SDK initialized successfully:', {
            sipUrl: this.sipUrl,
            didNumbers: this.didNumbers,
            extension: this.extension
          });

          this.setupEventListeners();

          resolve({
            sipUrl: this.sipUrl,
            didNumbers: this.didNumbers,
            extension: this.extension
          });
        } else {
          console.error('SDK returned no data');
          reject('Failed to initialize Ozonetel SDK - no data returned');
        }
      } catch (error) {
        console.error('Exception in initializeSDK:', error);
        reject(error);
      }
    });
  }

  startStreaming(): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log('OzonetelService: startStreaming called');
      console.log('isInitialized:', this.isInitialized);
      console.log('isStreaming:', this.isStreaming);

      if (!this.isInitialized || !this.oneCXI) {
        console.error('SDK not initialized');
        reject('SDK not initialized');
        return;
      }

      if (!this.didNumbers || this.didNumbers.length === 0) {
        console.error('No DID numbers available');
        reject('No DID numbers available');
        return;
      }

      if (this.isStreaming) {
        console.log('Already streaming');
        resolve({ status: 'Already streaming', didNumber: this.didNumbers[0] });
        return;
      }

      try {
        const didNumber = this.didNumbers[0];
        console.log('Starting streaming with DID:', didNumber);
        this.oneCXI.startStreaming(didNumber);
        this.isStreaming = true;
        console.log('Streaming started successfully');
        resolve({ status: 'Streaming started', didNumber });
      } catch (error) {
        console.error('Exception in startStreaming:', error);
        reject(error);
      }
    });
  }

  startCall(phoneNumber: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized || !this.oneCXI) {
        reject('SDK not initialized');
        return;
      }

      if (!this.isStreaming) {
        this.startStreaming()
          .then(() => {
            resolve({ status: 'Ready to dial', phoneNumber });
          })
          .catch(error => reject(error));
      } else {
        resolve({ status: 'Ready to dial', phoneNumber });
      }
    });
  }

  stopStreaming(): void {
    if (this.oneCXI && this.isInitialized && this.isStreaming) {
      this.oneCXI.stopStreaming();
      this.isStreaming = false;
    }
  }

  stopCall(): void {
    this.stopStreaming();
  }

  toggleMute(): void {
    if (this.oneCXI && this.isInitialized) {
      this.oneCXI.toggleMute();
    }
  }

  toggleHold(): void {
    if (this.oneCXI && this.isInitialized) {
      this.oneCXI.toggleHold();
    }
  }

  sendDTMF(digit: string): void {
    if (this.oneCXI && this.isInitialized) {
      this.oneCXI.sendDTMF(digit);
    }
  }

  isSDKInitialized(): boolean {
    return this.isInitialized;
  }

  isStreamingActive(): boolean {
    return this.isStreaming;
  }

  getDIDNumbers(): string[] {
    return this.didNumbers;
  }

  private setupEventListeners(): void {
    window.addEventListener('Started', (event: any) => {
      console.log('Ozonetel call started:', event);
      this.handleCallStarted(event);
    });

    window.addEventListener('Ended', (event: any) => {
      console.log('Ozonetel call ended:', event);
      this.handleCallEnded(event);
    });

    window.addEventListener('additionalText', (event: any) => {
      console.log('Ozonetel additional text:', event);
      this.handleAdditionalText(event);
    });

    window.addEventListener('additionalTextUser', (event: any) => {
      console.log('Ozonetel user speaking:', event);
      this.handleAdditionalTextUser(event);
    });
  }

  private handleCallStarted(event: any): void {
    window.dispatchEvent(new CustomEvent('ozonetel-call-started', { detail: event }));
  }

  private handleCallEnded(event: any): void {
    window.dispatchEvent(new CustomEvent('ozonetel-call-ended', { detail: event }));
  }

  private handleAdditionalText(event: any): void {
    window.dispatchEvent(new CustomEvent('ozonetel-additional-text', { detail: event }));
  }

  private handleAdditionalTextUser(event: any): void {
    window.dispatchEvent(new CustomEvent('ozonetel-user-speaking', { detail: event }));
  }

  agentManualDial(params: {
    phoneNumber: string;
    dueId: number;
    phoneNumberId: number;
    uui?: string;
    campaignName?: string;
  }): Observable<any> {
    console.log('[OzonetelService] Manual dial via backend API');

    const dial$ = () =>
      this.http.post(`${environment.api_url}/api/ozonetel/call/manual-dial`, params);

    if (shouldUseOzonetelSDK() && this.ozonetelSDKCore.isAgentInitialized()) {
      return this.dialAfterAgentReady(dial$);
    }

    return dial$();
  }

  sdkAgentManualDial(params: {
    phoneNumber: string;
    campaignName?: string;
    uui?: string;
  }): Observable<any> {
    console.log('[OzonetelService] SDK manual dial via backend API');

    const dial$ = () =>
      this.http.post(`${environment.api_url}/api/ozonetel/call/sdk-manual-dial`, params);

    if (shouldUseOzonetelSDK() && this.ozonetelSDKCore.isAgentInitialized()) {
      return this.dialAfterAgentReady(dial$);
    }

    return dial$();
  }

  private dialAfterAgentReady(dial$: () => Observable<any>): Observable<any> {
    return from(this.ozonetelSDKCore.ensureAgentReadyForManualDial()).pipe(
      switchMap(() => dial$()),
      catchError((error) => {
        this.ozonetelSDKCore.cancelManualDialAttempt();
        return throwError(() => error);
      })
    );
  }
}

